// Citations — a reply's källhänvisningar as pen "Chat – every entry" §2 draws
// them: numbered markers in the text ("Källmarkör"), and "N sources" at the
// end of the reply, folding open to a list (marker · title · page). A click on
// a marker or a row opens the source at that spot; the chat stays.
//
// Pure apart from the list's `open`, which is view-ephemeral. The footnote
// number (`index`) is the adapter's, so a marker and its row always agree.

export { CitationMarkers, SourcesList };

import { exhaustSwitchCase } from "@bilda/utils";
import { Plural, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Icon } from "../../Icon/Icon.js";
import {
    elementFocusTarget,
    pageFocusTarget,
} from "../../Navigation/focusTarget.js";
import type { FocusTarget, ResourceRef } from "../../Navigation/types.js";
import type { ChatCitation, CitationLocator } from "../types.js";
import "./Citations.css";

// Open a cited source, at a place in it when the citation names one.
type OpenCitation = (ref: ResourceRef, target?: FocusTarget) => void;

const targetOf = (locator: CitationLocator): FocusTarget | undefined => {
    switch (locator.type) {
        case "page":
            return pageFocusTarget(locator.page);
        case "heading":
            return undefined;
        case "element":
            return elementFocusTarget(locator.elementId);
        default:
            return exhaustSwitchCase(locator);
    }
};

const openAt = (citation: ChatCitation, open: OpenCitation) => () =>
    open(
        {
            type: "source",
            id: citation.resourceId,
            title: citation.sourceTitle,
        },
        targetOf(citation.locator),
    );

// Where in the source, in words ("p. 52", a section's name).
const LocatorText = ({
    locator,
}: {
    readonly locator: CitationLocator;
}): string => {
    const { t } = useLingui();
    switch (locator.type) {
        case "page": {
            const page = locator.page;
            return t`p. ${page}`;
        }
        case "heading":
            return locator.slug;
        case "element":
            return t`a passage`;
        default:
            return exhaustSwitchCase(locator);
    }
};

const Marker = ({
    citation,
    onOpen,
}: {
    readonly citation: ChatCitation;
    readonly onOpen?: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const { index, sourceTitle } = citation;
    const body = (
        <>
            <Icon name="sources" className="bilda-cite-marker__icon" />
            {index}
        </>
    );
    return onOpen === undefined ? (
        <span className="bilda-cite-marker">{body}</span>
    ) : (
        <button
            type="button"
            className="bilda-cite-marker"
            aria-label={t`Source ${index}: ${sourceTitle}`}
            onClick={onOpen}
        >
            {body}
        </button>
    );
};

// The markers, inline after the reply's text.
const CitationMarkers = ({
    citations,
    onOpen,
}: {
    readonly citations: readonly ChatCitation[];
    readonly onOpen: OpenCitation;
}): ReactNode => (
    <span className="bilda-cite-markers">
        {citations.map((c) => (
            <Marker key={c.id} citation={c} onOpen={openAt(c, onOpen)} />
        ))}
    </span>
);

// "N sources" at the end of the reply, folding open to one row per
// reference.
const SourcesList = ({
    citations,
    onOpen,
}: {
    readonly citations: readonly ChatCitation[];
    readonly onOpen: OpenCitation;
}): ReactNode => {
    const [open, setOpen] = useState(false);
    if (citations.length === 0) return null;
    return (
        <div className="bilda-cite-sources">
            <button
                type="button"
                className="bilda-cite-sources__head"
                aria-expanded={open}
                onClick={() => setOpen((shown) => !shown)}
            >
                <Icon name="sources" className="bilda-cite-sources__icon" />
                <Plural
                    value={citations.length}
                    one="# source"
                    other="# sources"
                />
                <Icon
                    name={open ? "chevron-up" : "chevron-down"}
                    className="bilda-cite-sources__chevron"
                />
            </button>
            {open ? (
                <ul className="bilda-cite-sources__list">
                    {citations.map((c) => (
                        <li key={c.id}>
                            <button
                                type="button"
                                className="bilda-cite-row"
                                onClick={openAt(c, onOpen)}
                            >
                                <Marker citation={c} />
                                <span className="bilda-cite-row__title">
                                    {c.sourceTitle}
                                </span>
                                <span className="bilda-cite-row__where">
                                    <LocatorText locator={c.locator} />
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};
